<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, Modal, ModalBody } from '@sveltestrap/sveltestrap';
  import { formatDistance } from 'date-fns';

  import { Icon } from '@client/components/icon';
  import { Timeline } from '@client/components/timeline';

  import { CampaignsService } from '@client/services/campaigns';

  import { consolidateMetrics, formatRate } from '@client/utils/banners';
  import { determineLookBackPeriod } from '@client/utils/campaigns';

  import { formatDate } from '@common/utils/dates';

  export let selectedDate = { events: [] };
  export let maxEventCount = 60;

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  let dateTimeFormat = 'EEE, MMM d, yy @ h:mm a';

  let selectedCampaign = null;
  let orderedCampaignsForDay = [];

  const loadCampaign = (campaign) => {
    selectedCampaign = campaign;
  };

  const cache = {};

  const getMetrics = async (campaign) => {
    if (!selectedCampaign) {
      return null;
    }

    const id = campaign._id;

    if (!cache[id]) {
      const days = determineLookBackPeriod(campaign);
      const data = await CampaignsService.getMetrics({ aid: campaign._id, days });

      cache[id] = {
        metrics: consolidateMetrics(data, campaign)
      };
    }

    return cache[id].metrics;
  };

  let currentIndex = 0;

  const nextCampaign = () => {
    const currentCampaignIndex = orderedCampaignsForDay.findIndex(({ _id }) => _id === selectedCampaign._id);
    const next = currentCampaignIndex + 1;

    metrics = null;

    if (next === orderedCampaignsForDay.length) {
      selectedCampaign = orderedCampaignsForDay[0];
    } else {
      selectedCampaign = orderedCampaignsForDay[next];
    }

    currentIndex = orderedCampaignsForDay.findIndex(({ _id }) => _id === selectedCampaign._id);
  };

  const previousCampaign = () => {
    const currentCampaignIndex = orderedCampaignsForDay.findIndex(({ _id }) => _id === selectedCampaign._id);
    const previous = currentCampaignIndex - 1;

    metrics = null;

    if (previous < 0) {
      selectedCampaign = orderedCampaignsForDay[orderedCampaignsForDay.length - 1];
    } else {
      selectedCampaign = orderedCampaignsForDay[previous];
    }

    currentIndex = orderedCampaignsForDay.findIndex(({ _id }) => _id === selectedCampaign._id);
  };

  document.addEventListener('keyup', ({ code }) => {
    const leftArrow = code === 'ArrowLeft';
    const rightArrow = code === 'ArrowRight';

    if (selectedCampaign && (rightArrow || leftArrow)) {
      if (leftArrow) {
        previousCampaign();
      } else {
        nextCampaign();
      }
    }
  });

  $: if (selectedDate) {
    selectedCampaign = selectedDate.events[0];
    orderedCampaignsForDay = selectedDate.events.sort(
      (a, b) => new Date(a.schedule.startDate) - new Date(b.schedule.startDate)
    );
  }

  const calculateTimeRemaining = () => {
    if (selectedCampaign) {
      const startDate = new Date(selectedCampaign.schedule.startDate);
      const endDate = new Date(selectedCampaign.schedule.endDate);
      const now = new Date();

      if (endDate.getTime() > now.getTime()) {
        return $format('label.CAMPAIGN_STARTED', { values: { time: formatDistance(now, endDate) } });
      }

      if (startDate.getTime() > now.getTime()) {
        return $format('label.CAMPAIGN_NOT_STARTED', { values: { time: formatDistance(now, startDate) } });
      }

      if (endDate.getTime() < now.getTime()) {
        return $format('label.CAMPAIGN_COMPLETED');
      }

      return '';
    }

    return '';
  };

  let metrics = null;

  const fetch = async () => {
    metrics = await getMetrics(selectedCampaign);

    return metrics;
  };

  $: fetch(selectedCampaign);
  $: timeRemaining = calculateTimeRemaining(selectedCampaign);
</script>

<Modal isOpen={selectedDate} fade={false} size="lg" class="adjustme">
  <ModalBody>
    <div class="calendar-events">
      <button class="calendar-events-close" on:click={() => (selectedDate = null)}>
        <Icon name="x" />
      </button>

      <div class="text-center">
        <h1>{$format('label.SCHEDULED_CAMPAIGNS')}</h1>
        <h4>{selectedDate.dateLabel}</h4>
      </div>

      {#if selectedCampaign}
        <div class="campaign-card">
          <Icon name="chevron-left" role="button" tabindex="0" on:click={previousCampaign} />
          <div class="campaign-card--details">
            <div class="edit-campaign">
              <Button
                color="secondary"
                size="sm"
                on:click={() => redirect(`/marketing/campaigns/${selectedCampaign.rulename}`)}
              >
                {$format('label.EDIT')}
              </Button>
            </div>

            {#if selectedCampaign.adtype === 'image'}
              <img class="banner" src={selectedCampaign.previewURL} alt={selectedCampaign.alttext} />
            {:else}
              <div class="banner">
                {selectedCampaign.textstring}
              </div>
            {/if}

            <div class="name">
              {selectedCampaign.rulename}
              <small>({timeRemaining})</small>
            </div>
            <div class="time">
              {formatDate(selectedCampaign.schedule.startDate, dateTimeFormat)} - {formatDate(
                selectedCampaign.schedule.endDate,
                dateTimeFormat
              )}
            </div>
            <ul class="tags">
              {#each selectedCampaign.tags as tag}
                <li>{tag.text}</li>
              {/each}
            </ul>
            <div class="metrics">
              <div class="metric">
                {#if metrics}
                  <b>{metrics.totals.ad.toLocaleString()}</b>
                {:else}
                  <b>-</b>
                {/if}
                <div>{$format('label.DELIVERED')}</div>
              </div>
              <div class="metric">
                {#if metrics}
                  <b>
                    {metrics.totals.combinedViews.toLocaleString()}
                  </b>
                {:else}
                  <b>-</b>
                {/if}
                <div>{$format('label.IMPRESSIONS')}</div>
              </div>
              <div class="metric">
                {#if metrics}
                  <b>
                    {metrics.totals.combinedClicks.toLocaleString()}
                    <small>(CTR {formatRate(metrics.totals.combinedCtr)})</small>
                  </b>
                {:else}
                  <b>-</b>
                {/if}
                <div>{$format('label.CLICKS')}</div>
              </div>
              <div class="metric">
                {#if metrics}
                  <b>{metrics.calculated.reach.toLocaleString()}</b>
                {:else}
                  <b>-</b>
                {/if}
                <div>{$format('label.REACH')}</div>
              </div>
            </div>
          </div>

          <Icon name="chevron-right" role="button" tabindex="0" on:click={nextCampaign} />
        </div>
        {#if selectedDate.events.length > maxEventCount}
          <div class="current-index">{$format('label.CAMPAIGN')} {currentIndex + 1}/{selectedDate.events.length}</div>
        {/if}
      {/if}

      <Timeline
        {selectedDate}
        bind:selectedCampaign
        bind:orderedCampaignsForDay
        onClick={loadCampaign}
        {maxEventCount}
      />
    </div>
  </ModalBody>
</Modal>

<style lang="scss" src="./campaigns-modal.scss"></style>
